import { TranslationKey, useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { ReactElement, useContext, useState } from 'react';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Modal } from '../../../../design/modals/modal/S_Modal';
import { AdminContext } from '../../../../modules/admin/AdminContext';
import { getEmailStatus } from '../../../helpers/email.helpers';
import { TranslateServerErrorCodes } from '../../../helpers/serverErrorsTranslator.helper';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { currentTimeZone } from '../../../models/timeZones/timeZones.model';
import { useAuthService } from '../../auth/auth.service';
import { NotificationLevel, ToastNotification } from '../../notifications/models/notification.model';
import { INotificationsService } from '../../notifications/services/notifications.service';
import { IUserService } from '../../user/user.service';
import { RegisterUserForm } from '../components/RegisterUserForm';

interface IProps {
  showModal: boolean;
  onClose: () => void;
}

export const RegisterUserContainer = (props: IProps): ReactElement => {
  const authService = useAuthService();
  const userService = useInjection(IUserService.$);
  const notificationsService = useInjection(INotificationsService.$);
  const context = useContext(AdminContext);
  const { withLoaderAsync } = useLoader();
  const { t } = useTranslator();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('');
  const [emailStatus, setEmailStatus] = useState(InputStatus.empty());
  const [passwordStatus, setPasswordStatus] = useState(InputStatus.empty());
  const [externalStatus, setExternalStatus] = useState(InputStatus.empty());
  const [isRegistrationPending, setIsRegistrationPending] = useState(false);

  const handleEmailChange = (value: string): void => {
    setEmail(value);
    setEmailStatus(InputStatus.empty());
    setExternalStatus(InputStatus.empty());
  };

  const handlePasswordChange = (value: string): void => {
    setPassword(value);
    setExternalStatus(InputStatus.empty());
  };

  const handleCompanyChange = (value: string): void => {
    setCompany(value);
    setExternalStatus(InputStatus.empty());
  };

  const handleCountryChange = (value: string): void => {
    setCountry(value);
    setExternalStatus(InputStatus.empty());
  };

  const handlePasswordStatusChange = (status: InputStatus): void => {
    setPasswordStatus(status);
  };

  const handleClose = (): void => {
    setEmail('');
    setPassword('');
    setCompany('');
    setCountry('');
    setEmailStatus(InputStatus.empty());
    setPasswordStatus(InputStatus.empty());
    setExternalStatus(InputStatus.empty());
    setIsRegistrationPending(false);
    props.onClose();
  };

  const validateEmail = (value: string): boolean | undefined => {
    const status = getEmailStatus(value, []);
    setEmailStatus(status);
    return status.isValid;
  };

  const handleRegister = async (): Promise<void> => {
    const isEmailValid = validateEmail(email);
    const isPasswordValid = passwordStatus.isValid;
    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setIsRegistrationPending(true);

    await withLoaderAsync(registerUser, 'create-account-button');
  };

  const registerUser = async (): Promise<void> => {
    const currentTimeZoneOffset = currentTimeZone?.value || 0;
    const result = await authService.adminRegister({
      email,
      password,
      company,
      country,
      timeZoneOffset: currentTimeZoneOffset,
    });

    if (result instanceof ACDLError) {
      const errorCodes: TranslationKey[] = result.apiErrorResponse ? TranslateServerErrorCodes(result.apiErrorResponse.errorCodes) : ['something_went_wrong'];
      setExternalStatus(new InputStatus(false, errorCodes));
    } else {
      await notificationsService.push(new ToastNotification(NotificationLevel.SUCCESS, 'new_user_created_successfully'));
      await withLoaderAsync(async () => {
        await userService.getUsers(context.search);
        await userService.getPlanDefaultsAsync();
      });

      handleClose();
    }
    setIsRegistrationPending(false);
  };

  const validationErrorOccurred = externalStatus.isValid === false || emailStatus.isValid === false || passwordStatus.isValid === false;

  return (
    <S_Modal
      isOpen={props.showModal}
      toggle={handleClose}
      headerText={t('new_user')}
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <ButtonWithLoader
            loaderKey='create-account-button'
            color='primary'
            disabled={validationErrorOccurred || isRegistrationPending}
            loading={isRegistrationPending}
            onClick={handleRegister}
          >
            {t('add_user')}
          </ButtonWithLoader>
        </>
      }
    >
      <RegisterUserForm
        emailStatus={emailStatus}
        passwordStatus={passwordStatus}
        externalStatus={externalStatus}
        email={email}
        company={company}
        country={country}
        onCompanyChange={handleCompanyChange}
        onCountryChange={handleCountryChange}
        onEmailChange={handleEmailChange}
        onPasswordChange={handlePasswordChange}
        onPasswordStatusChange={handlePasswordStatusChange}
        validateEmail={validateEmail}
      />
    </S_Modal>
  );
};
